import React, {useEffect, useMemo, useState} from "react";
import {useMutation, useQuery} from "@apollo/client";
import {useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import NavBar from "../../components/common/NavBar";
import {confirmDelete} from "../../components/common/confirmDelete";
import MarketplaceListingCardList from "../../components/marketplace/MarketplaceListingCardList";
import MarketplaceListingCard from "../../components/marketplace/MarketplaceListingCard";
import MarketplaceListingPromotionModal from "../../components/marketplace/MarketplaceListingPromotionModal";
import MarketplaceListingFilters from "../../components/marketplace/MarketplaceListingFilters";
import MarketplacePageHeader from "../../components/marketplace/MarketplacePageHeader";
import MarketplaceMetricCards from "../../components/marketplace/MarketplaceMetricCards";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import {useBranding} from "../../context/BrandingContext";
import useTranslation from "../../i18n/useTranslation";
import {alphaFromHex} from "../../util/colors";
import {
    MARKETPLACE_LISTING_CREATION_ACCESS,
    MARKETPLACE_CATEGORIES,
    MY_MARKETPLACE_LISTINGS,
    CHANGE_MARKETPLACE_LISTING_STATUS,
} from "../../components/marketplace/queries";

function MarketplacePriceRange({minPrice, maxPrice, onMinChange, onMaxChange, currency, colors}) {
    const maximumPrice = 1000;
    const minimumValue = minPrice === "" ? 0 : Number(minPrice);
    const maximumValue = maxPrice === "" ? maximumPrice : Number(maxPrice);

    return <div className="marketplace-price-range" style={{color: "#14151a"}}>
        <div className="d-flex justify-content-between align-items-center small font-weight-bold mb-1">
            <span>{minimumValue} {currency}</span>
            <span>{maximumValue >= maximumPrice ? `${maximumPrice}+ ${currency}` : `${maximumValue} ${currency}`}</span>
        </div>
        <div className="marketplace-price-range-track">
            <div className="marketplace-price-range-fill" style={{
                left: `${(minimumValue / maximumPrice) * 100}%`,
                right: `${100 - (maximumValue / maximumPrice) * 100}%`,
                background: colors.secondary
            }}/>
            <input aria-label="Minimum price" type="range" min="0" max={maximumPrice} step="5" value={minimumValue}
                   onChange={(event) => onMinChange(Math.min(Number(event.target.value), maximumValue))}/>
            <input aria-label="Maximum price" type="range" min="0" max={maximumPrice} step="5" value={maximumValue}
                   onChange={(event) => onMaxChange(Math.max(Number(event.target.value), minimumValue))}/>
        </div>
    </div>;
}

export default function MarketplaceManagementPage() {
    const {t} = useTranslation();
    const {theme} = useBranding();
    const colors = theme?.colors || {};
    const {activeProfile} = useActiveProfile();
    const navigate = useNavigate();
    const [status, setStatus] = useState("");
    const [listingType, setListingType] = useState("");
    const [condition, setCondition] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const [sportId, setSportId] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [country, setCountry] = useState("");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [appliedFilters, setAppliedFilters] = useState({});
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [page, setPage] = useState(1);
    const [promotionListing, setPromotionListing] = useState(null);
    const pageSize = 15;
    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPage(1);
        }, 350);
        return () => window.clearTimeout(timer);
    }, [search]);
    const currency = String(activeProfile?.currency || "usd").toUpperCase();
    const queryVariables = useMemo(() => ({
        categoryId: appliedFilters.categoryId ? Number(appliedFilters.categoryId) : null,
        sportId: appliedFilters.sportId ? Number(appliedFilters.sportId) : null,
        listingType: appliedFilters.listingType || null,
        condition: appliedFilters.condition || null,
        status: appliedFilters.status || null,
        city: appliedFilters.city || null,
        state: appliedFilters.state || null,
        country: appliedFilters.country || null,
        minPrice: appliedFilters.minPrice || null,
        maxPrice: appliedFilters.maxPrice || null,
        search: debouncedSearch || null,
        page,
        pageSize,
    }), [appliedFilters, debouncedSearch, page]);
    const {data, loading, error} = useQuery(MY_MARKETPLACE_LISTINGS, {
        variables: queryVariables,
        fetchPolicy: "cache-and-network",
    });
    const {data: accessData} = useQuery(MARKETPLACE_LISTING_CREATION_ACCESS, {
        fetchPolicy: "cache-and-network",
    });
    const {data: categoriesData} = useQuery(MARKETPLACE_CATEGORIES, {fetchPolicy: "cache-first"});
    const [changeListingStatus] = useMutation(CHANGE_MARKETPLACE_LISTING_STATUS, {
        refetchQueries: [MY_MARKETPLACE_LISTINGS],
        awaitRefetchQueries: true
    });

    const pagination = data?.marketplaceMyListings;
    const allListings = useMemo(() => pagination?.objects || [], [pagination?.objects]);
    const categories = categoriesData?.marketplaceCategories || [];
    const listings = allListings;
    const access = accessData?.marketplaceListingCreationAccess;
    const statusCounts = pagination?.statusCounts || {};
    const totalListings = Object.values(statusCounts).reduce((total, count) => total + Number(count || 0), 0);
    const listingUsageText = access?.listingLimit == null
        ? `${access?.activeListings ?? totalListings} active listings`
        : `${access?.activeListings ?? totalListings} / ${access.listingLimit} active listings`;
    const countByStatus = (value) => Number(statusCounts[value] || 0);
    const applyFilters = () => {
        setPage(1);
        setAppliedFilters({
            status,
            listingType,
            condition,
            categoryId,
            sportId,
            city: city.trim(),
            state: state.trim(),
            country: country.trim(),
            minPrice,
            maxPrice
        });
    };
    const selectStatus = (value) => {
        setStatus(value);
        setPage(1);
        setAppliedFilters((filters) => ({...filters, status: value}));
    };
    const resetFilters = () => {
        setStatus("");
        setListingType("");
        setCondition("");
        setCategoryId("");
        setSportId("");
        setCity("");
        setState("");
        setCountry("");
        setMinPrice("");
        setMaxPrice("");
        setSearch("");
        setDebouncedSearch("");
        setPage(1);
        setAppliedFilters({});
    };
    const updateListingStatus = async (listing, nextStatus) => {
        if (!window.confirm(t("marketplaceManagementPage.actions.confirmStatus", {status: t(`marketplaceManagementPage.statuses.${nextStatus}`)}))) return;
        try {
            await changeListingStatus({
                variables: {
                    id: Number(listing.id),
                    status: nextStatus,
                    reason: `Status changed to ${nextStatus} by seller`
                }
            });
            toast.success(t("marketplaceManagementPage.statusUpdated"));
        } catch (mutationError) {
            toast.error(mutationError.message);
        }
    };
    const removeListing = async (listing) => {
        const confirmed = await confirmDelete({
            title: t("marketplaceManagementPage.actions.remove"),
            message: t("marketplaceManagementPage.actions.confirmRemove"),
            confirmLabel: t("marketplaceManagementPage.actions.remove"),
            cancelLabel: t("common.cancel"),
        });
        if (!confirmed) return;
        try {
            await changeListingStatus({
                variables: {
                    id: Number(listing.id),
                    status: "removed",
                    reason: "Removed by seller"
                }
            });
            toast.success(t("marketplaceManagementPage.statusUpdated"));
        } catch (mutationError) {
            toast.error(mutationError.message);
        }
    };

    return (
        <div className="content-wrapper">
            <NavBar navBarElem={{
                pageTitle: t("marketplaceManagementPage.title"),
                links: [{pageTitle: t("marketplaceManagementPage.title"), active: true}]
            }}/>
            <section className="content">
                <div className="container-fluid">
                    <MarketplacePageHeader title={t("marketplaceManagementPage.title")}
                                           subtitle={t("marketplaceManagementPage.subtitle")} colors={colors}>
                        <div className="d-flex align-items-center" style={{
                            gap: 12,
                            minWidth: 220,
                            marginLeft: "auto",
                            padding: "10px 14px",
                            borderRadius: 14,
                            background: `${colors.primary}0b`,
                            border: `1px solid ${colors.primary}20`
                        }}>
                            <div className="d-flex align-items-center justify-content-center" style={{
                                width: 40,
                                height: 40,
                                borderRadius: 12,
                                background: colors.primary,
                                color: colors.textOnPrimary
                            }}><i className="fas fa-list"/></div>
                            <div>
                                <div
                                    className="small text-muted font-weight-bold">{t("marketplaceManagementPage.listingUsage")}</div>
                                <strong style={{
                                    display: "block",
                                    color: colors.primary,
                                    fontSize: 17
                                }}>{listingUsageText}</strong></div>
                        </div>
                    </MarketplacePageHeader>

                    <MarketplaceMetricCards colors={colors} selectedKey={appliedFilters.status || ""}
                                            onSelect={selectStatus} metrics={[
                        {
                            key: "",
                            label: t("marketplaceManagementPage.allStatuses"),
                            icon: "fa-store",
                            count: totalListings
                        },
                        {
                            key: "available",
                            label: t("marketplaceManagementPage.statuses.available"),
                            icon: "fa-check-circle",
                            count: countByStatus("available")
                        },
                        {
                            key: "sold",
                            label: t("marketplaceManagementPage.statuses.sold"),
                            icon: "fa-tag",
                            count: countByStatus("sold")
                        },
                        {
                            key: "rented",
                            label: t("marketplaceManagementPage.statuses.rented"),
                            icon: "fa-calendar-alt",
                            count: countByStatus("rented")
                        },
                    ].map(({key, label, icon, count}) => ({key: key || "", label, icon, value: count}))}/>

                    {error ? <div className="alert alert-danger">{error.message}</div> : null}

                    <MarketplaceListingFilters
                        colors={colors}
                        search={search}
                        onSearchChange={setSearch}
                        expanded={filtersExpanded}
                        onToggle={() => setFiltersExpanded((expanded) => !expanded)}
                        onClear={resetFilters}
                        onApply={applyFilters}
                        categories={categories}
                        values={{listingType, condition, status, categoryId, sportId, city, state, country}}
                        onChange={(field, value) => {
                            const setters = {
                                listingType: setListingType,
                                condition: setCondition,
                                status: setStatus,
                                categoryId: setCategoryId,
                                sportId: setSportId,
                                city: setCity,
                                state: setState,
                                country: setCountry
                            };
                            setters[field](value);
                            setPage(1);
                        }}
                        showCondition
                        showStatus
                        showLocationDetails
                        showPrice
                        priceRange={{
                            label: `${t("marketplaceManagementPage.filters.minPrice")} — ${t("marketplaceManagementPage.filters.maxPrice")}`,
                            content: <MarketplacePriceRange minPrice={minPrice} maxPrice={maxPrice}
                                                            onMinChange={(value) => setMinPrice(String(value))}
                                                            onMaxChange={(value) => setMaxPrice(value >= 1000 ? "" : String(value))}
                                                            currency={currency} colors={colors}/>
                        }}
                    />

                    <MarketplaceListingCardList
                        title={t("marketplaceManagementPage.title")}
                        subtitle={`${totalListings} ${t("marketplaceManagementPage.title").toLowerCase()}`}
                        onAdd={access?.canCreate ? () => navigate("/marketplace/new") : null}
                        addButtonTitle={t("marketplaceManagementPage.createListing")}
                        items={listings}
                        pagination={pagination}
                        onPageChange={setPage}
                        loading={loading}
                        emptyText={t("marketplaceManagementPage.noListings")}
                        renderItem={(listing) => <MarketplaceListingCard listing={listing}
                                                                         onView={(item) => navigate(`/marketplace/${item.id}`)}
                                                                         onEdit={(item) => navigate(`/marketplace/${item.id}/edit`)}
                                                                         onStatusChange={updateListingStatus}
                                                                         onRemove={removeListing}
                                                                         onPromote={(item) => item.promotionCampaignId ? navigate(`/advertise/campaign/${encodeURIComponent(item.promotionCampaignId)}/edit`) : setPromotionListing(item)}/>}
                    />
                    <MarketplaceListingPromotionModal listing={promotionListing}
                                                      onClose={() => setPromotionListing(null)}
                                                      onPromoted={(campaign) => {
                                                          setPromotionListing(null);
                                                          if (campaign?.id) navigate(`/advertise/campaign/${encodeURIComponent(campaign.id)}/edit`);
                                                      }}/>
                </div>
            </section>
            <style>{`.marketplace-management-filter-panel{padding:12px;border-radius:18px;background:linear-gradient(135deg,${alphaFromHex(colors.primary, 0.10)},${alphaFromHex(colors.secondary, 0.08)});border:1px solid ${alphaFromHex(colors.primary, 0.14)}}.marketplace-management-filter-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));align-items:end;gap:12px}.marketplace-management-filter-field label{display:block;margin:0 0 6px;color:${colors.textOnPrimary};font-size:.72rem;font-weight:900;letter-spacing:.05em;text-transform:uppercase}.marketplace-management-filter-control{display:flex;align-items:center;gap:10px;min-height:46px;padding:0 12px;border-radius:14px;background:#fff;border:1px solid;box-shadow:0 8px 18px rgba(17,24,39,.06);transition:border-color .15s ease,box-shadow .15s ease,transform .15s ease}.marketplace-management-filter-control:focus-within{box-shadow:0 0 0 4px ${alphaFromHex(colors.primary, 0.12)},0 10px 22px rgba(17,24,39,.08);transform:translateY(-1px)}.marketplace-management-filter-control select{appearance:none;cursor:pointer}.marketplace-price-range{width:100%;min-width:0}.marketplace-price-range-track{position:relative;height:24px;margin:0 2px}.marketplace-price-range-track:before{content:"";position:absolute;top:10px;left:0;right:0;height:4px;border-radius:999px;background:#e5e7eb}.marketplace-price-range-fill{position:absolute;top:10px;height:4px;border-radius:999px;z-index:1}.marketplace-price-range-track input{position:absolute;top:0;left:0;width:100%;height:24px;margin:0;background:transparent;appearance:none;pointer-events:none;z-index:2}.marketplace-price-range-track input::-webkit-slider-runnable-track{height:4px;background:transparent}.marketplace-price-range-track input::-moz-range-track{height:4px;background:transparent}.marketplace-price-range-track input::-webkit-slider-thumb{width:17px;height:17px;margin-top:-6px;border:3px solid #fff;border-radius:50%;background:${colors.secondary};box-shadow:0 2px 7px rgba(17,24,39,.25);appearance:none;pointer-events:auto;cursor:pointer}.marketplace-price-range-track input::-moz-range-thumb{width:12px;height:12px;border:3px solid #fff;border-radius:50%;background:${colors.secondary};box-shadow:0 2px 7px rgba(17,24,39,.25);pointer-events:auto;cursor:pointer}.marketplace-management-filter-actions{grid-column:1 / -1;display:flex;justify-content:flex-end;gap:8px;align-items:stretch}.marketplace-management-filter-reset{min-height:46px;padding:10px 12px;border:1px solid;font-weight:900;display:inline-flex;align-items:center;justify-content:center;gap:8px;border-radius:12px;box-shadow:0 10px 22px rgba(17,24,39,.16);cursor:pointer;white-space:nowrap}.marketplace-management-filter-reset:hover{transform:translateY(-1px)}@media(max-width:1199.98px){.marketplace-management-filter-grid{grid-template-columns:repeat(3,minmax(0,1fr))}.marketplace-price-range-field{grid-column:span 2!important}}@media(max-width:767.98px){.marketplace-management-filter-grid{grid-template-columns:1fr}.marketplace-price-range-field{grid-column:span 1!important}.marketplace-management-filter-actions{justify-content:flex-end;flex-wrap:wrap}}`}</style>
        </div>
    );
}
